import { MsgBoxBack } from '@/util/msgutil/msgBox'; // 自定义弹框工具
import { request, router } from '@/util/globalContext'; // 全局路由/请求工具
import { ValidConst } from '@/util/validConst'; // 验证常量
import { enumSelect } from '@/util/vueutil/commonList'; // 枚举工具

// ===================== 初始化相关（页面加载核心逻辑） =====================
/**
 * 初始化页面核心数据（加载用户+菜单数据）
 * @param {Object} context 组件上下文（包含响应式数据/方法）
 * @param {String} roleId 角色ID（可选）
 */
export const initPageData = async (context, roleId) => {
    // 初始化当前时间并定时更新
    updateCurrentTime(context);
    context.timeInterval = setInterval(() => updateCurrentTime(context), 1000);

    // 加载用户和菜单数据
    await loadAllUserData(context, roleId);

    // 初始化页面标题
    updatePageTitle(context, context.route.path);

    // 绑定全局点击事件（关闭下拉菜单）
    bindGlobalClickEvent(context);

    // 绑定窗口大小变化事件（调整下拉菜单位置）
    bindWindowResizeEvent(context);

    // 监听路由变化
    watchRouteChange(context);
};

/**
 * 加载用户所有数据（菜单+用户信息）
 * @param {Object} context 组件上下文
 * @param {String} roleId 角色ID
 */
export const loadAllUserData = async (context, roleId) => {
    try {
        const params = roleId ? { roleId } : {};
        const res = await request.get('/home/getUserRole', { params });
        if (res) {
            if (res.code !== 200) {
                MsgBoxBack.error('获取菜单失败,即将返回登录页', () => {
                    router.push('/login'); // 返回登录页
                });
                return;
            }
            // 设置用户信息
            const userData = res.data;
            context.userInfo = {
                userId: userData.userId || '',
                username: userData.username || '',
                avatar: userData.avatar || '',
                roles: userData.roles || []
            };
            sessionStorage.setItem('userId', userData.userId || '');
            // 设置默认角色
            context.currentRole = context.userInfo.roles && context.userInfo.roles.length ? context.userInfo.roles[0] : { roleName: '普通用户' };
            sessionStorage.setItem('currentRole', context.currentRole.roleId);

            // 设置菜单列表（无需初始化展开状态，树组件默认关闭）
            context.menuList = userData.menuList || [];
            // 更新页面标题
            updatePageTitle(context, context.activeMenuUrl);
        }
    } catch (err) {
        MsgBoxBack.error('获取菜单失败,即将返回登录页', () => {
            router.push('/login'); // 返回登录页
        });
    }
};

// ===================== 菜单排序/交互相关 =====================
/**
 * 菜单排序：首页（sortNum=-1）置顶，其余按sortNum升序排列
 * @param {Array} menus 原始菜单列表
 * @returns {Array} 排序后的菜单列表
 */
export const sortMenus = (menus) => {
    if (!Array.isArray(menus)) return [];

    // 分离首页和其他菜单
    const homeMenu = menus.find(item => Number(item.sortNum) == -1) || [];
    const otherMenus = menus.filter(item => Number(item.sortNum) !== -1);

    // 其他菜单按sortNum升序排序
    const sortedOtherMenus = otherMenus.sort((a, b) => a.sortNum - b.sortNum);

    // 递归处理子菜单排序
    const recursiveSort = (menuList) => {
        return menuList.map(menu => {
            const newMenu = { ...menu };
            if (newMenu.children && newMenu.children.length) {
                newMenu.children = recursiveSort(newMenu.children);
            }
            return newMenu;
        });
    };

    // 组合最终菜单（首页+排序后的其他菜单）
    const finalMenus = homeMenu ? [homeMenu, ...recursiveSort(sortedOtherMenus)] : recursiveSort(sortedOtherMenus);
    return finalMenus;
};

/**
 * 处理树形菜单点击事件
 * @param {Object} data 节点数据
 * @param {Object} node 节点实例
 * @param {Object} vm 组件上下文（通过arguments透传）
 */
export const handleTreeMenuClick = function(context,data) {
    // 菜单类型：路由跳转
    if ( data.menuType == '2') {
        context.activeMenuUrl = data.menuUrl;
        router.push(data.menuUrl);
        updatePageTitle(context, data.menuUrl);
    }
};

/**
 * 切换角色
 * @param {Object} context 组件上下文
 * @param {Object} role 选中的角色
 */
export const changeRole = async (context, role) => {
    context.currentRole = role;
    sessionStorage.setItem('currentRole', context.currentRole.roleId);
    context.isRoleMenuShow = false;

    // 重新请求对应角色的菜单数据
    await loadAllUserData(context, role.roleId);
    context.$message.success(`已切换至${role.roleName}角色`);
    //切换完后回到首页
    router.push('/home')
};

// ===================== 页面辅助功能 =====================
/**
 * 更新当前时间
 * @param {Object} context 组件上下文
 */
export const updateCurrentTime = (context) => {
    const now = new Date();
    context.currentTime = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

/**
 * 更新页面标题（面包屑格式）
 * @param {Object} context 组件上下文
 * @param {String} url 路由路径
 */
export const updatePageTitle = (context, url) => {
    if (url === '/home') {
        context.currentPageTitle = '首页';
        return;
    }

    // 递归查找当前URL对应的菜单路径
    const findMenuPath = (menus, targetUrl, path = []) => {
        for (const menu of menus) {
            if (menu.menuUrl === targetUrl) {
                return [...path, menu.menuName];
            }
            if (menu.children && menu.children.length) {
                const result = findMenuPath(menu.children, targetUrl, [...path, menu.menuName]);
                if (result) return result;
            }
        }
        return null;
    };

    const path = findMenuPath(context.menuList, url);
    context.currentPageTitle = path ? path.join(' > ') : '未知页面';
};

// ===================== 事件绑定/监听相关 =====================
/**
 * 绑定全局点击事件（关闭下拉菜单）
 * @param {Object} context 组件上下文
 */
export const bindGlobalClickEvent = (context) => {
    document.addEventListener('click', (e) => {
        // 关闭头像下拉菜单
        if (context.isAvatarMenuShow && !context.avatarRef.value?.contains(e.target)) {
            context.isAvatarMenuShow = false;
        }

        // 关闭角色下拉菜单
        const roleWrapper = context.$el.querySelector('.home-user-role-wrapper');
        if (context.isRoleMenuShow && !roleWrapper?.contains(e.target)) {
            context.isRoleMenuShow = false;
        }
    });
};

/**
 * 绑定窗口大小变化事件（调整下拉菜单位置）
 * @param {Object} context 组件上下文
 */
export const bindWindowResizeEvent = (context) => {
    window.addEventListener('resize', () => {
        if (context.isAvatarMenuShow) {
            // 触发重绘
            context.isAvatarMenuShow = false;
            context.$nextTick(() => {
                context.isAvatarMenuShow = true;
            });
        }
    });
};

/**
 * 监听路由变化
 * @param {Object} context 组件上下文
 */
export const watchRouteChange = (context) => {
    context.routeListener = context.$watch(
        () => context.route.path,
        (newPath) => {
            context.activeMenuUrl = newPath;
            updatePageTitle(context, newPath);
        }
    );
};

// ===================== 工具方法 =====================
/**
 * 查看我的信息
 * @param {Object} userInfo 用户信息
 */
export const showUserInfo = (userInfo) => {
    if (!userInfo) return;

    let infoText = "我的信息：\n";
    Object.entries(userInfo).forEach(([key, value]) => {
        infoText += `${key}：${value}\n`;
    });
    alert(infoText);
};

/**
 * 加载公司类型枚举
 * @param {Object} context 组件上下文
 */
export const loadCompanyTypes = async (context) => {
    try {
        context.companyTypeList = await enumSelect('enumType', 'company_type');
    } catch (err) {
        console.error('获取公司类型枚举失败', err);
    }
};

/**
 * 根据公司类型code获取名称
 * @param {Object} context 组件上下文
 * @param {String} code 公司类型code
 */
export const getCompanyTypeName = (context, code) => {
    if (!code) return '';
    const type = context.companyTypeList?.find(item => item.code === code);
    return type ? type.enumCodeName : code;
};

/**
 * 加载用户详细信息
 * @param {Object} context 组件上下文
 */
export const loadUserDetail = async (context) => {
    try {
        // 先加载公司类型枚举
        await loadCompanyTypes(context);
        const res = await request.get('/home/getUserDetail');
        if (res && res.code === 200) {
            context.userDetail = res.data;
        } else {
            context.$message.error(res?.msg || '获取用户信息失败');
        }
    } catch (err) {
        console.error('获取用户信息失败', err);
        context.$message.error('获取用户信息失败');
    }
};

/**
 * 表单验证规则
 */
export const getCompanyFormRules = () => {
    return {
        companyName: [
            { required: true, message: '请输入公司名称', trigger: 'blur' },
            { max: 64, message: '公司名称最多64个字', trigger: 'blur' }
        ],
        companyType: [
            { required: true, message: '请选择公司类型', trigger: 'change' }
        ],
        contactPhone: [
            { required: true, message: '请输入联系电话', trigger: 'blur' },
            { pattern: ValidConst.PHONE_REG, message: ValidConst.PHONE_MSG, trigger: 'blur' }
        ],
        companyAddress: [
            { max: 200, message: '公司地址最多200个字', trigger: 'blur' }
        ]
    };
};

/**
 * 保存公司（新增/修改）
 * @param {Object} context 组件上下文
 */
export const saveCompany = async (context) => {
    try {
        const formRef = context.$refs.companyFormRef;
        if (!formRef) {
            // 如果 ref 不存在，直接提交
            const res = await request.post('/home/saveCompany', context.companyForm);
            if (res && res.code === 200) {
                context.$message.success(res.msg || '保存成功');
                context.showCompanyDialog = false;
                await loadUserDetail(context);
            } else {
                context.$message.error(res?.msg || '保存失败');
            }
            return;
        }

        await formRef.validate(async (valid) => {
            if (valid) {
                const res = await request.post('/home/saveCompany', context.companyForm);
                if (res && res.code === 200) {
                    context.$message.success(res.msg || '保存成功');
                    context.showCompanyDialog = false;
                    await loadUserDetail(context);
                } else {
                    context.$message.error(res?.msg || '保存失败');
                }
            }
        });
    } catch (err) {
        console.error('保存公司失败', err);
        context.$message.error('保存失败');
    }
};

/**
 * 删除公司
 * @param {Object} context 组件上下文
 * @param {Object} params 包含 companyId 的对象
 */
export const deleteCompany = async (context, params) => {
    try {
        const res = await request.post('/home/deleteCompany', params);
        if (res && res.code === 200) {
            context.$message.success(res.msg || '删除成功');
            await loadUserDetail(context);
        } else {
            context.$message.error(res?.msg || '删除失败');
        }
    } catch (err) {
        console.error('删除公司失败', err);
        context.$message.error('删除失败');
    }
};

/**
 * 退出登录
 */
export const logout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("currentRole");
    window.location.href = "/login";
};

// ===================== 清理资源相关 =====================
/**
 * 清理页面资源（定时器/事件监听）
 * @param {Object} context 组件上下文
 */
export const cleanPageResources = (context) => {
    // 清除时间更新定时器
    clearInterval(context.timeInterval);
    // 清除头像菜单定时器
    clearTimeout(context.avatarTimer);
    // 移除窗口大小变化监听
    window.removeEventListener('resize', () => {});
    // 停止路由监听
    if (context.routeListener) {
        context.routeListener();
    }
};